'use client';

import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, FileUp, Plus, Save, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useControls } from '@/features/controls/api/use-controls';
import { DEFAULT_CONTROL_FILTERS } from '@/features/controls/types';
import type { Control, ControlFilters } from '@/features/controls/types';
import { useMembers } from '@/features/team/api/use-members';
import type { OrganizationMember } from '@/features/team/types';
import {
  useTrainingRecord,
  useUpdateTrainingRecord,
} from '@/features/workforce-readiness/api/use-training-records';
import { LinkedEvidencePanel } from '@/features/workforce-readiness/components/LinkedEvidencePanel';
import {
  type EmployeeCompletionStatus,
  type TrainingAgendaItem,
  type TrainingRecord,
  type TrainingRecordStatus,
  type TrainingResourceLink,
} from '@/features/workforce-readiness/data';
import {
  buildTrainingRecordInput,
  mapTrainingRecordFromApi,
} from '@/features/workforce-readiness/mappers';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';

interface ParticipantProgressDraftValue {
  completionStatus: EmployeeCompletionStatus;
  completionDate: string | null;
  evidenceLabel: string | null;
}

interface TrainingWorkspaceDraft {
  trainingName: string;
  trainingType: string;
  description: string;
  status: TrainingRecordStatus;
  date: string;
  selectedParticipantIds: string[];
  participantProgress: Record<string, ParticipantProgressDraftValue>;
  selectedControlIds: string[];
  agendaItems: TrainingAgendaItem[];
  resourceLinks: TrainingResourceLink[];
}

export const Route = createFileRoute(
  '/_authenticated/workforce-readiness/training-register/$trainingId'
)({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.workforceReadinessTrainingWorkspace'),
  }),
  component: TrainingWorkspacePage,
});

function TrainingWorkspacePage() {
  const { t } = useTranslation('common');
  const { trainingId } = Route.useParams();
  const navigate = useNavigate();
  const trainingQuery = useTrainingRecord(trainingId);
  const training = useMemo(
    () => (trainingQuery.record ? mapTrainingRecordFromApi(trainingQuery.record) : null),
    [trainingQuery.record]
  );
  const updateTrainingMutation = useUpdateTrainingRecord();

  const [draft, setDraft] = useState<TrainingWorkspaceDraft | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [participantSearch, setParticipantSearch] = useState('');
  const [controlSearch, setControlSearch] = useState('');

  const { organizations, isLoading: orgsLoading } = useOrganizations();
  const activeOrg = organizations.find((org) => org.isActive);
  const canManageAssignments = Boolean(activeOrg?.id);

  const membersQueryOptions = useMemo(
    () => ({
      orgId: activeOrg?.id ?? '',
      page: 1,
      pageSize: 300,
      sortBy: 'name' as const,
      sortOrder: 'asc' as const,
      enabled: Boolean(activeOrg?.id),
    }),
    [activeOrg?.id]
  );
  const { data: membersData, isLoading: membersLoading } = useMembers(membersQueryOptions);
  const availableMembers = useMemo(() => membersData?.members ?? [], [membersData?.members]);

  const controlsFilters = useMemo<ControlFilters>(
    () => ({
      ...DEFAULT_CONTROL_FILTERS,
      sortBy: 'title',
      sortOrder: 'asc',
    }),
    []
  );

  const { controls: availableControls, isLoading: controlsLoading } = useControls({
    filters: controlsFilters,
    page: 1,
    pageSize: 300,
    enabled: Boolean(activeOrg?.id),
  });

  const membersByUserId = useMemo(
    () => new Map(availableMembers.map((member) => [member.userId, member])),
    [availableMembers]
  );
  const controlsById = useMemo(
    () => new Map(availableControls.map((control) => [control.id, control])),
    [availableControls]
  );

  useEffect(() => {
    if (!training) {
      setDraft(null);
      return;
    }
    setDraft(buildWorkspaceDraft(training));
    setSaveError(null);
    setSaveNotice(null);
    setParticipantSearch('');
    setControlSearch('');
  }, [training]);

  const filteredMembers = useMemo(() => {
    const query = participantSearch.trim().toLowerCase();
    if (!query) {
      return availableMembers;
    }
    return availableMembers.filter((member) => {
      const displayName = getMemberDisplayName(member).toLowerCase();
      const email = member.email.toLowerCase();
      return displayName.includes(query) || email.includes(query);
    });
  }, [availableMembers, participantSearch]);

  const filteredControls = useMemo(() => {
    const query = controlSearch.trim().toLowerCase();
    if (!query) {
      return availableControls;
    }
    return availableControls.filter((control) => {
      const title = control.title.toLowerCase();
      const code = control.controlCode.toLowerCase();
      return title.includes(query) || code.includes(query);
    });
  }, [availableControls, controlSearch]);

  const isDirty = useMemo(() => {
    if (!training || !draft) {
      return false;
    }
    return (
      JSON.stringify(toComparableRecord(training)) !== JSON.stringify(toComparableDraft(draft))
    );
  }, [draft, training]);

  const updateDraft = (updater: (current: TrainingWorkspaceDraft) => TrainingWorkspaceDraft) => {
    setDraft((current) => (current ? updater(current) : current));
    setSaveNotice(null);
  };

  const upsertParticipantSelection = (userId: string, checked: boolean) => {
    updateDraft((current) => {
      const selectedParticipantIds = checked
        ? uniqueValues([...current.selectedParticipantIds, userId])
        : current.selectedParticipantIds.filter((id) => id !== userId);

      if (checked) {
        const participantProgress = {
          ...current.participantProgress,
        };
        participantProgress[userId] ??= {
          completionStatus: 'not_started',
          completionDate: null,
          evidenceLabel: null,
        };

        return {
          ...current,
          selectedParticipantIds,
          participantProgress,
        };
      }

      const participantProgress = Object.fromEntries(
        Object.entries(current.participantProgress).filter(([id]) => id !== userId)
      ) as Record<string, ParticipantProgressDraftValue>;

      return {
        ...current,
        selectedParticipantIds,
        participantProgress,
      };
    });
  };

  const updateParticipantProgress = (
    userId: string,
    patch: Partial<ParticipantProgressDraftValue>
  ) => {
    updateDraft((current) => {
      const previousValue = current.participantProgress[userId] ?? {
        completionStatus: 'not_started' as const,
        completionDate: null,
        evidenceLabel: null,
      };

      return {
        ...current,
        participantProgress: {
          ...current.participantProgress,
          [userId]: {
            ...previousValue,
            ...patch,
          },
        },
      };
    });
  };

  const upsertControlSelection = (controlId: string, checked: boolean) => {
    updateDraft((current) => ({
      ...current,
      selectedControlIds: checked
        ? uniqueValues([...current.selectedControlIds, controlId])
        : current.selectedControlIds.filter((id) => id !== controlId),
    }));
  };

  const updateAgendaItem = (agendaId: string, patch: Partial<TrainingAgendaItem>) => {
    updateDraft((current) => ({
      ...current,
      agendaItems: current.agendaItems.map((item) =>
        item.id === agendaId
          ? {
              ...item,
              ...patch,
            }
          : item
      ),
    }));
  };

  const updateResourceLink = (linkId: string, patch: Partial<TrainingResourceLink>) => {
    updateDraft((current) => ({
      ...current,
      resourceLinks: current.resourceLinks.map((item) =>
        item.id === linkId
          ? {
              ...item,
              ...patch,
            }
          : item
      ),
    }));
  };

  const saveTraining = async () => {
    if (!training || !draft) {
      return;
    }

    const trainingName = draft.trainingName.trim();
    const trainingType = draft.trainingType.trim();
    if (!trainingName) {
      setSaveError('Training name is required.');
      return;
    }
    if (!trainingType) {
      setSaveError('Training type is required.');
      return;
    }
    if (!draft.date) {
      setSaveError('Planned date is required.');
      return;
    }

    const nextTraining: TrainingRecord = {
      ...training,
      trainingName,
      trainingType,
      description: draft.description.trim(),
      status: draft.status,
      date: draft.date,
      assignedEmployees: draft.selectedParticipantIds.map((userId) => {
        const progress = draft.participantProgress[userId] ?? {
          completionStatus: 'not_started' as const,
          completionDate: null,
          evidenceLabel: null,
        };

        return {
          userId,
          completionStatus: progress.completionStatus,
          completionDate: progress.completionDate,
          evidenceLabel: progress.evidenceLabel,
        };
      }),
      linkedControlIds: uniqueValues(draft.selectedControlIds),
      agendaItems: draft.agendaItems
        .map((item) => ({
          ...item,
          title: item.title.trim(),
          sessionDate: item.sessionDate.trim(),
          startTime: item.startTime.trim(),
          endTime: item.endTime.trim(),
          notes: item.notes.trim(),
        }))
        .filter((item) => item.title.length > 0),
      resourceLinks: draft.resourceLinks
        .map((item) => ({
          ...item,
          label: item.label.trim(),
          url: item.url.trim(),
        }))
        .filter((item) => item.label.length > 0 && item.url.length > 0),
    };

    try {
      await updateTrainingMutation.updateRecord({
        recordId: training.id,
        data: buildTrainingRecordInput(nextTraining),
      });
      setSaveError(null);
      setSaveNotice(t('workforceReadiness.trainingWorkspace.savedNotice'));
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save training.');
    }
  };

  if (orgsLoading || trainingQuery.isLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-[1300px] space-y-4">
          <div className="h-10 w-48 animate-pulse rounded bg-muted" />
          <div className="h-48 animate-pulse rounded bg-muted" />
          <div className="h-96 animate-pulse rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (trainingQuery.isError) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-3xl">
          <Card>
            <CardContent className="py-6 text-sm text-destructive">
              {trainingQuery.error?.message ?? 'Failed to load training workspace.'}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!training || !draft) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-3xl space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('workforceReadiness.trainingWorkspace.trainingNotFound')}</CardTitle>
              <CardDescription>
                {t('workforceReadiness.trainingWorkspace.trainingNotFoundDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild>
                <Link to="/workforce-readiness/training-register">{t('workforceReadiness.trainingWorkspace.backToTrainingRegister')}</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-[1300px] space-y-6">
        <Button variant="ghost" size="sm" asChild className="-ml-2">
          <Link to="/workforce-readiness/training-register">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('workforceReadiness.trainingWorkspace.trainingRegister')}
          </Link>
        </Button>

        <Card className="sticky top-2 z-10 border-2">
          <CardContent className="space-y-4 pt-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('workforceReadiness.trainingWorkspace.trainingWorkspace')}
                </p>
                <h1 className="text-2xl font-semibold tracking-tight">{draft.trainingName}</h1>
                <p className="text-sm text-muted-foreground">
                  {draft.trainingType || t('workforceReadiness.trainingWorkspace.addTrainingType')} · {t('workforceReadiness.trainingWorkspace.planned', { date: formatDate(draft.date) })}
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-1">
                  <Label className="text-xs">{t('workforceReadiness.trainingWorkspace.statusLabel')}</Label>
                  <Select
                    value={draft.status}
                    options={[
                      { value: 'planned', label: t('workforceReadiness.trainingWorkspace.plannedOption') },
                      { value: 'active', label: t('workforceReadiness.trainingWorkspace.activeOption') },
                      { value: 'completed', label: t('workforceReadiness.trainingWorkspace.completedOption') },
                      { value: 'archived', label: t('workforceReadiness.trainingWorkspace.archivedOption') },
                    ]}
                    onChange={(value) => {
                      updateDraft((current) => ({
                        ...current,
                        status: value as TrainingRecordStatus,
                      }));
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('workforceReadiness.trainingWorkspace.plannedDateLabel')}</Label>
                  <Input
                    type="date"
                    value={draft.date}
                    onChange={(event) => {
                      const nextDate = event.target.value;
                      updateDraft((current) => ({
                        ...current,
                        date: nextDate,
                        agendaItems: current.agendaItems.map((item) => ({
                          ...item,
                          sessionDate: item.sessionDate || nextDate,
                        })),
                      }));
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('workforceReadiness.trainingWorkspace.participantsLabel')}</Label>
                  <div className="flex h-9 items-center rounded-md border px-3 text-sm font-medium">
                    {String(draft.selectedParticipantIds.length)}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('workforceReadiness.trainingWorkspace.controlsLabel')}</Label>
                  <div className="flex h-9 items-center rounded-md border px-3 text-sm font-medium">
                    {String(draft.selectedControlIds.length)}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={getTrainingStatusBadgeVariant(draft.status)}>
                {toLabel(draft.status)}
              </Badge>
              <Badge variant="outline">
                {draft.status === 'completed' ? t('workforceReadiness.trainingWorkspace.done') : t('workforceReadiness.trainingWorkspace.inProgressPlanned')}
              </Badge>
              <Badge variant="outline">{t('workforceReadiness.trainingWorkspace.agendaItems', { count: draft.agendaItems.length })}</Badge>
              <Badge variant="outline">
                {t('workforceReadiness.trainingWorkspace.evidenceEntries', {
                  count: training.evidenceCount ?? 0,
                })}
              </Badge>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => {
                  void saveTraining();
                }}
                disabled={!isDirty || updateTrainingMutation.isUpdating}
              >
                <Save className="mr-2 h-4 w-4" />
                {updateTrainingMutation.isUpdating
                  ? 'Saving...'
                  : t('workforceReadiness.trainingWorkspace.saveChanges')}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  void navigate({
                    to: '/audit/evidence-upload',
                    search: {
                      linkedObjectType: 'training_record',
                      linkedObjectId: training.id,
                      complianceContext: `Training: ${draft.trainingName}`,
                    },
                  });
                }}
              >
                <FileUp className="mr-2 h-4 w-4" />
                {t('workforceReadiness.trainingWorkspace.addEvidence')}
              </Button>
              <Button variant="outline" asChild>
                <Link to="/audit/evidence-library">{t('workforceReadiness.trainingWorkspace.evidenceLibrary')}</Link>
              </Button>
            </div>

            {saveError ? (
              <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {saveError}
              </p>
            ) : null}
            {saveNotice ? (
              <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                {saveNotice}
              </p>
            ) : null}
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="h-auto w-full flex-wrap justify-start gap-1 bg-transparent p-0">
            <TabsTrigger value="overview">{t('workforceReadiness.trainingWorkspace.overviewTab')}</TabsTrigger>
            <TabsTrigger value="participants">{t('workforceReadiness.trainingWorkspace.participantsTab')}</TabsTrigger>
            <TabsTrigger value="agenda-links">{t('workforceReadiness.trainingWorkspace.agendaLinksTab')}</TabsTrigger>
            <TabsTrigger value="evidence">{t('workforceReadiness.trainingWorkspace.evidenceTab')}</TabsTrigger>
            <TabsTrigger value="controls">{t('workforceReadiness.trainingWorkspace.controlsTab')}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('workforceReadiness.trainingWorkspace.trainingOverview')}</CardTitle>
                <CardDescription>
                  {t('workforceReadiness.trainingWorkspace.trainingOverviewDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="training-workspace-name">{t('workforceReadiness.trainingWorkspace.trainingNameLabel')}</Label>
                    <Input
                      id="training-workspace-name"
                      value={draft.trainingName}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        updateDraft((current) => ({
                          ...current,
                          trainingName: nextValue,
                        }));
                      }}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="training-workspace-type">{t('workforceReadiness.trainingWorkspace.trainingTypeLabel')}</Label>
                    <Input
                      id="training-workspace-type"
                      value={draft.trainingType}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        updateDraft((current) => ({
                          ...current,
                          trainingType: nextValue,
                        }));
                      }}
                    />
                  </div>
                </div>

                <label className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                  <Checkbox
                    checked={draft.status === 'completed'}
                    onCheckedChange={(checked) => {
                      updateDraft((current) => ({
                        ...current,
                        status: checked === true ? 'completed' : 'active',
                      }));
                    }}
                  />
                  {t('workforceReadiness.trainingWorkspace.markTrainingCompleted')}
                </label>

                <div className="space-y-1.5">
                  <Label htmlFor="training-workspace-description">{t('workforceReadiness.trainingWorkspace.descriptionLabel')}</Label>
                  <Textarea
                    id="training-workspace-description"
                    rows={4}
                    value={draft.description}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      updateDraft((current) => ({
                        ...current,
                        description: nextValue,
                      }));
                    }}
                    placeholder={t('workforceReadiness.trainingWorkspace.descriptionPlaceholder')}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="participants" className="space-y-4">
            <div className="grid gap-4 xl:grid-cols-2">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{t('workforceReadiness.trainingWorkspace.employees')}</CardTitle>
                  <CardDescription>{t('workforceReadiness.trainingWorkspace.employeesDesc')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Input
                    value={participantSearch}
                    onChange={(event) => {
                      setParticipantSearch(event.target.value);
                    }}
                    placeholder={t('workforceReadiness.trainingWorkspace.searchEmployees')}
                    disabled={!canManageAssignments}
                  />
                  <div className="max-h-72 space-y-2 overflow-auto rounded-md border p-2">
                    {!canManageAssignments ? (
                      <p className="text-sm text-muted-foreground">
                        {t('workforceReadiness.trainingWorkspace.selectOrgToLoadEmployees')}
                      </p>
                    ) : membersLoading ? (
                      <p className="text-sm text-muted-foreground">{t('workforceReadiness.trainingWorkspace.loadingParticipants')}</p>
                    ) : filteredMembers.length === 0 ? (
                      <p className="text-sm text-muted-foreground">{t('workforceReadiness.trainingWorkspace.noMatchingEmployees')}</p>
                    ) : (
                      filteredMembers.map((member) => {
                        const checked = draft.selectedParticipantIds.includes(member.userId);
                        return (
                          <label
                            key={member.userId}
                            className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-muted"
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(value) => {
                                upsertParticipantSelection(member.userId, value === true);
                              }}
                            />
                            <span className="text-sm">
                              {getMemberDisplayName(member)}
                              <span className="ml-1 text-xs text-muted-foreground">
                                ({member.email})
                              </span>
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{t('workforceReadiness.trainingWorkspace.participantStatus')}</CardTitle>
                  <CardDescription>
                    {t('workforceReadiness.trainingWorkspace.participantStatusDesc')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {draft.selectedParticipantIds.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {t('workforceReadiness.trainingWorkspace.selectParticipantsToManage')}
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {draft.selectedParticipantIds.map((userId) => {
                        const member = membersByUserId.get(userId);
                        const displayName = getMemberDisplayName(member, userId);
                        const progress = draft.participantProgress[userId] ?? {
                          completionStatus: 'not_started' as const,
                          completionDate: null,
                          evidenceLabel: null,
                        };

                        return (
                          <div key={userId} className="rounded-md border p-3">
                            <p className="mb-2 text-sm font-medium">{displayName}</p>
                            <div className="grid gap-2 md:grid-cols-3">
                              <Select
                                value={progress.completionStatus}
                                options={[
                                  { value: 'not_started', label: t('workforceReadiness.trainingWorkspace.notStarted') },
                                  { value: 'in_progress', label: t('workforceReadiness.trainingWorkspace.inProgress') },
                                  { value: 'completed', label: t('workforceReadiness.trainingWorkspace.completedStatus') },
                                ]}
                                onChange={(value) => {
                                  updateParticipantProgress(userId, {
                                    completionStatus: value as EmployeeCompletionStatus,
                                  });
                                }}
                              />
                              <Input
                                type="date"
                                value={progress.completionDate ?? ''}
                                onChange={(event) => {
                                  updateParticipantProgress(userId, {
                                    completionDate: event.target.value || null,
                                  });
                                }}
                              />
                              <Input
                                value={progress.evidenceLabel ?? ''}
                                onChange={(event) => {
                                  updateParticipantProgress(userId, {
                                    evidenceLabel: event.target.value || null,
                                  });
                                }}
                                placeholder={t('workforceReadiness.trainingWorkspace.attendancePlaceholder')}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="agenda-links" className="space-y-4">
            <div className="grid gap-4 xl:grid-cols-2">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>{t('workforceReadiness.trainingWorkspace.agenda')}</CardTitle>
                    <CardDescription>{t('workforceReadiness.trainingWorkspace.agendaDesc')}</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      updateDraft((current) => ({
                        ...current,
                        agendaItems: [
                          ...current.agendaItems,
                          createTrainingAgendaDraft(current.date),
                        ],
                      }));
                    }}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    {t('workforceReadiness.trainingWorkspace.addItem')}
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  {draft.agendaItems.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('workforceReadiness.trainingWorkspace.noAgendaItems')}</p>
                  ) : (
                    draft.agendaItems.map((item) => (
                      <div key={item.id} className="rounded-md border p-3">
                        <div className="grid gap-2 md:grid-cols-2">
                          <Input
                            value={item.title}
                            onChange={(event) => {
                              updateAgendaItem(item.id, { title: event.target.value });
                            }}
                            placeholder={t('workforceReadiness.trainingWorkspace.agendaTopicPlaceholder')}
                          />
                          <Input
                            type="date"
                            value={item.sessionDate}
                            onChange={(event) => {
                              updateAgendaItem(item.id, { sessionDate: event.target.value });
                            }}
                          />
                          <Input
                            type="time"
                            value={item.startTime}
                            onChange={(event) => {
                              updateAgendaItem(item.id, { startTime: event.target.value });
                            }}
                          />
                          <Input
                            type="time"
                            value={item.endTime}
                            onChange={(event) => {
                              updateAgendaItem(item.id, { endTime: event.target.value });
                            }}
                          />
                        </div>
                        <Textarea
                          className="mt-2"
                          rows={2}
                          value={item.notes}
                          onChange={(event) => {
                            updateAgendaItem(item.id, { notes: event.target.value });
                          }}
                          placeholder={t('workforceReadiness.trainingWorkspace.contextPlaceholder')}
                        />
                        <div className="mt-2 flex justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              updateDraft((current) => ({
                                ...current,
                                agendaItems: current.agendaItems.filter(
                                  (agenda) => agenda.id !== item.id
                                ),
                              }));
                            }}
                          >
                            <Trash2 className="mr-1 h-3.5 w-3.5" />
                            {t('workforceReadiness.trainingWorkspace.remove')}
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>{t('workforceReadiness.trainingWorkspace.linksTitle')}</CardTitle>
                    <CardDescription>
                      {t('workforceReadiness.trainingWorkspace.linksDesc')}
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      updateDraft((current) => ({
                        ...current,
                        resourceLinks: [...current.resourceLinks, createTrainingResourceDraft()],
                      }));
                    }}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    {t('workforceReadiness.trainingWorkspace.addLink')}
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  {draft.resourceLinks.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('workforceReadiness.trainingWorkspace.noLinksAttached')}</p>
                  ) : (
                    draft.resourceLinks.map((link) => (
                      <div key={link.id} className="rounded-md border p-3">
                        <div className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
                          <Input
                            value={link.label}
                            onChange={(event) => {
                              updateResourceLink(link.id, { label: event.target.value });
                            }}
                            placeholder={t('workforceReadiness.trainingWorkspace.labelPlaceholder')}
                          />
                          <Input
                            value={link.url}
                            onChange={(event) => {
                              updateResourceLink(link.id, { url: event.target.value });
                            }}
                            placeholder="https://..."
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              updateDraft((current) => ({
                                ...current,
                                resourceLinks: current.resourceLinks.filter(
                                  (item) => item.id !== link.id
                                ),
                              }));
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        {link.url ? (
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-2 inline-block text-xs text-primary hover:underline"
                          >
                            {t('workforceReadiness.trainingWorkspace.openLink')}
                          </a>
                        ) : null}
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="evidence" className="space-y-4">
            <LinkedEvidencePanel
              linkedObjectType="training_record"
              linkedObjectId={training.id}
              linkedObjectName={`Training: ${draft.trainingName}`}
              emptyMessage={t('workforceReadiness.trainingWorkspace.noEvidenceEntries')}
            />
          </TabsContent>

          <TabsContent value="controls" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('workforceReadiness.trainingWorkspace.linkedControls')}</CardTitle>
                <CardDescription>{t('workforceReadiness.trainingWorkspace.linkedControlsDesc')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Input
                  value={controlSearch}
                  onChange={(event) => {
                    setControlSearch(event.target.value);
                  }}
                  placeholder={t('workforceReadiness.trainingWorkspace.searchControls')}
                  disabled={!canManageAssignments}
                />
                <div className="max-h-72 space-y-2 overflow-auto rounded-md border p-2">
                  {!canManageAssignments ? (
                    <p className="text-sm text-muted-foreground">
                      {t('workforceReadiness.trainingWorkspace.selectOrgToLoadControls')}
                    </p>
                  ) : controlsLoading ? (
                    <p className="text-sm text-muted-foreground">{t('workforceReadiness.trainingWorkspace.loadingControls')}</p>
                  ) : filteredControls.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('workforceReadiness.trainingWorkspace.noMatchingControls')}</p>
                  ) : (
                    filteredControls.map((control) => {
                      const checked = draft.selectedControlIds.includes(control.id);
                      return (
                        <label
                          key={control.id}
                          className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-muted"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(value) => {
                              upsertControlSelection(control.id, value === true);
                            }}
                          />
                          <span className="text-sm">{getControlLabel(control, control.id)}</span>
                        </label>
                      );
                    })
                  )}
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-medium">{t('workforceReadiness.trainingWorkspace.currentlyLinked')}</p>
                  {draft.selectedControlIds.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('workforceReadiness.trainingWorkspace.noControlsLinked')}</p>
                  ) : (
                    <div className="space-y-1.5">
                      {draft.selectedControlIds.map((controlId) => {
                        const control = controlsById.get(controlId);
                        return (
                          <Link
                            key={controlId}
                            to="/controls/$controlId"
                            params={{ controlId }}
                            className="block text-sm text-primary hover:underline"
                          >
                            {getControlLabel(control, controlId)}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function buildWorkspaceDraft(training: TrainingRecord): TrainingWorkspaceDraft {
  const participantProgress: Record<string, ParticipantProgressDraftValue> = {};
  for (const participant of training.assignedEmployees) {
    participantProgress[participant.userId] = {
      completionStatus: participant.completionStatus,
      completionDate: participant.completionDate,
      evidenceLabel: participant.evidenceLabel,
    };
  }

  return {
    trainingName: training.trainingName,
    trainingType: training.trainingType,
    description: training.description,
    status: training.status,
    date: training.date,
    selectedParticipantIds: training.assignedEmployees.map((participant) => participant.userId),
    participantProgress,
    selectedControlIds: [...training.linkedControlIds],
    agendaItems: training.agendaItems.map((item) => ({ ...item })),
    resourceLinks: training.resourceLinks.map((item) => ({ ...item })),
  };
}

function toComparableRecord(training: TrainingRecord) {
  return {
    trainingName: training.trainingName,
    trainingType: training.trainingType,
    description: training.description,
    status: training.status,
    date: training.date,
    assignedEmployees: training.assignedEmployees,
    linkedControlIds: training.linkedControlIds,
    agendaItems: training.agendaItems,
    resourceLinks: training.resourceLinks,
  };
}

function toComparableDraft(draft: TrainingWorkspaceDraft) {
  return {
    trainingName: draft.trainingName,
    trainingType: draft.trainingType,
    description: draft.description,
    status: draft.status,
    date: draft.date,
    assignedEmployees: draft.selectedParticipantIds.map((userId) => ({
      userId,
      completionStatus: draft.participantProgress[userId]?.completionStatus ?? 'not_started',
      completionDate: draft.participantProgress[userId]?.completionDate ?? null,
      evidenceLabel: draft.participantProgress[userId]?.evidenceLabel ?? null,
    })),
    linkedControlIds: draft.selectedControlIds,
    agendaItems: draft.agendaItems,
    resourceLinks: draft.resourceLinks,
  };
}

function createTrainingAgendaDraft(sessionDate: string): TrainingAgendaItem {
  return {
    id: crypto.randomUUID(),
    title: '',
    sessionDate,
    startTime: '',
    endTime: '',
    notes: '',
  };
}

function createTrainingResourceDraft(): TrainingResourceLink {
  return {
    id: crypto.randomUUID(),
    label: '',
    url: '',
  };
}

function uniqueValues(values: string[]): string[] {
  return Array.from(new Set(values));
}

function getTrainingStatusBadgeVariant(
  status: TrainingRecordStatus
): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (status === 'active') {
    return 'default';
  }
  if (status === 'completed') {
    return 'secondary';
  }
  if (status === 'planned') {
    return 'outline';
  }
  return 'destructive';
}

function getMemberDisplayName(
  member: OrganizationMember | undefined,
  fallbackUserId?: string
): string {
  if (!member) {
    return fallbackUserId ?? 'Unknown user';
  }

  if (member.displayName) {
    return member.displayName;
  }

  const firstName = member.firstName?.trim() ?? '';
  const lastName = member.lastName?.trim() ?? '';
  const fullName = `${firstName} ${lastName}`.trim();

  if (fullName) {
    return fullName;
  }

  return member.email;
}

function getControlLabel(control: Control | undefined, fallbackControlId: string): string {
  if (!control) {
    return fallbackControlId;
  }

  return `${control.controlCode} - ${control.title}`;
}

function toLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatDate(value: string): string {
  if (!value) {
    return 'Not scheduled';
  }
  return new Date(value).toLocaleDateString();
}
